import { computed, effectScope, type Ref } from 'vue'

import { useCookie, useNuxtApp, useRuntimeConfig } from 'nuxt/app'

/** Temporary Levelup-2026 master flag — remove after launch. Enable: `document.cookie = 'cp-levelup-2026=true; path=/'` */
const COOKIE_KEY = 'cp-levelup-2026'

const isTruthyFlag = (value: unknown): boolean => {
  if (value === true || value === 1) return true
  if (typeof value !== 'string') return false
  const normalized = value.trim().toLowerCase()
  return normalized === 'true' || normalized === '1'
}

/**
 * Temporary master UI gate for Levelup-2026 features. Delete this file after launch day.
 *
 * Enabled when either:
 * - `NUXT_PUBLIC_LEVELUP_2026_ENABLED` / `runtimeConfig.public.levelUp2026Enabled` is true (env), or
 * - cookie `cp-levelup-2026` is true (per-browser override; SSR-shared)
 */
type NuxtAppWithLevelUpCookie = ReturnType<typeof useNuxtApp> & { _levelUp2026Cookie?: Ref<string | null> }

/**
 * One cookie ref per Nuxt app. On the client every `useCookie` call adds a cookie-change listener that
 * is only removed with an active effect scope, and route middleware (which reads this flag through
 * `useAppAccess`) has none — so creating it per call would add a listener on every navigation. The
 * detached scope keeps a single listener for the app's lifetime.
 */
const getLevelUp2026Cookie = (): Ref<string | null> => {
  const nuxtApp = useNuxtApp() as NuxtAppWithLevelUpCookie
  nuxtApp._levelUp2026Cookie ??= effectScope(true).run(() =>
    useCookie<string | null>(COOKIE_KEY, {
      default: () => null,
      sameSite: 'lax',
      path: '/',
    })
  )
  return nuxtApp._levelUp2026Cookie as Ref<string | null>
}

export const useLevelUp2026 = () => {
  const runtimeConfig = useRuntimeConfig()
  const levelUp2026Cookie = getLevelUp2026Cookie()

  const isLevelUp2026Enabled = computed(() => {
    // This is a flag to enable the feature globally.
    if (isTruthyFlag(runtimeConfig.public.levelUp2026Enabled)) {
      return true
    }
    // If not enabled globally, then can be enabled per-browser via cookie.
    return isTruthyFlag(levelUp2026Cookie.value)
  })

  return { isLevelUp2026Enabled }
}
